import React, { useEffect, useState } from "react";
import "./PrivacyPolicy.css";
import { Languages } from "lucide-react";

const PrivacyPolicy = () => {
  const [language, setLanguage] = useState("sq"); // 'sq' for Albanian, 'en' for English

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const toggleLanguage = () => {
    setLanguage((prev) => (prev === "sq" ? "en" : "sq"));
  };

  const content = {
    sq: {
      title: "Politika e Privatësisë",
      location: "Lipjan, Kosovë",
      effectiveDate: "Data e hyrjes në fuqi: 19/03/2026",
      sections: [
        {
          title: "1. Hyrje",
          paragraphs: [
            "Kjo Politikë e Privatësisë shpjegon mënyrën se si Katrori ITS mbledh, përdor, ruan dhe mbron të dhënat personale të përdoruesve të ueb-faqes së saj zyrtare, në përputhje me legjislacionin në fuqi për mbrojtjen e të dhënave personale.",
            "Ne jemi të përkushtuar të respektojmë privatësinë tuaj dhe të përpunojmë të dhënat personale në përputhje me Ligjin Nr. 06/L-082 për Mbrojtjen e të Dhënave Personale.",
          ],
        },
        {
          title: "2. Të dhënat që mbledhim",
          paragraphs: [
            "Faqja jonë nuk përdor cookies dhe nuk mbledh automatikisht të dhëna personale gjatë vizitës.",
            "Ne mbledhim të dhëna personale vetëm në rastet kur ju i jepni ato vullnetarisht përmes formularit të kontaktit përfshirë:",
          ],
          list: [
            "Emrin",
            "Email adresën",
            "Subjektin e mesazhit",
            "Përmbajtjen e mesazhit",
          ],
          additional: [
            "Përpunimi i këtyre të dhënave bëhet vetëm pasi ju keni dhënë pëlqimin tuaj përmes checkbox-it (kutisë së zgjedhjes) përkatëse.",
            "Katrori ITS është kontrollues i të dhënave personale të mbledhura përmes kësë ueb-faqeje.",
          ],
        },
        {
          title: "3. Qëllimi i përpunimit",
          paragraphs: ["Të dhënat e mbledhura përdoren vetëm për:"],
          list: [
            "T'iu përgjigjur kërkesave dhe pyetjeve tuaja",
            "Të komunikojmë me ju lidhur me interesimin tuaj",
          ],
        },
        {
          title: "4. Baza ligjore",
          paragraphs: [
            "Përpunimi i të dhënave personale bazohet në pëlqimin tuaj, i dhënë përmes formularit të kontaktit.",
          ],
        },
        {
          title: "5. Cookies",
          paragraphs: [
            "Ueb-faqja jonë nuk përdor cookies apo teknologji gjurmimi.",
          ],
        },
        {
          title: "6. Ruajtja e të dhënave",
          paragraphs: [
            "Të dhënat personale ruhen për aq kohë sa është e nevojshme për trajtimin e kërkesës suaj ose sipas kërkesave ligjore.",
          ],
        },
        {
          title: "7. Shpërndarja e të dhënave",
          paragraphs: [
            "Katrori ITS nuk i ndan të dhënat tuaja personale me palë të treta, përveç nëse kjo kërkohet me ligj.",
          ],
        },
        {
          title: "8. Të drejtat tuaja",
          paragraphs: ["Në përputhje me ligjin, ju keni të drejtë të:"],
          list: [
            "Kërkoni qasje në të dhënat tuaja",
            "Korrigjoni të dhënat e pasakta",
            "Kërkoni fshirjen e të dhënave",
            "Kërkoni kufizimin e përpunimit",
            "Tërhiqni pëlqimin tuaj në çdo kohë",
          ],
          additional: [
            <>
              Për ushtrimin e këtyre të drejtave, na kontaktoni në:{" "}
              <a href="mailto:info@katrori-its.com">info@katrori-its.com</a>
            </>,
          ],
        },
        {
          title: "9. Siguria e të dhënave",
          paragraphs: [
            "Ne ndërmarrim masa teknike dhe organizative për të mbrojtur të dhënat tuaja nga qasja e paautorizuar apo keqpërdorimi.",
          ],
        },
        {
          title: "10. Ndryshimet në politikë",
          paragraphs: [
            "Ne mund ta përditësojmë këtë politikë kohë pas kohe. Çdo ndryshim do të publikohet në këtë faqe.",
          ],
        },
        {
          title: "11. Kontakti",
          paragraphs: [
            "Për çdo kërkesë lidhur me të dhënat personale, mund të na kontaktoni në:",
          ],
          list: [
            <>
              Email:{" "}
              <a href="mailto:info@katrori-its.com">info@katrori-its.com</a>
            </>,
            "Adresë: Lipjan, Kosovë",
            <>
              Tel: <a href="tel:+38338606806">+383 (0) 38 606 806</a>
            </>,
          ],
        },
      ],
    },
    en: {
      title: "Privacy Policy",
      location: "Lipjan, Kosovo",
      effectiveDate: "Effective Date: 03/19/2026",
      sections: [
        {
          title: "1. Introduction",
          paragraphs: [
            "This Privacy Policy explains how Katrori ITS collects, uses, stores and protects the personal data of users of its official website, in accordance with current legislation on personal data protection.",
            "We are committed to respecting your privacy and processing personal data in accordance with Law No. 06/L-082 on Personal Data Protection.",
          ],
        },
        {
          title: "2. Data We Collect",
          paragraphs: [
            "Our website does not use cookies and does not automatically collect personal data during your visit.",
            "We collect personal data only when you voluntarily provide it through the contact form, including:",
          ],
          list: ["Name", "Email address", "Message subject", "Message content"],
          additional: [
            "The processing of this data is done only after you have given your consent through the corresponding checkbox.",
            "Katrori ITS is the controller of personal data collected through this website.",
          ],
        },
        {
          title: "3. Purpose of Processing",
          paragraphs: ["The collected data is used only to:"],
          list: [
            "Respond to your requests and questions",
            "Communicate with you regarding your interest",
          ],
        },
        {
          title: "4. Legal Basis",
          paragraphs: [
            "The processing of personal data is based on your consent, given through the contact form.",
          ],
        },
        {
          title: "5. Cookies",
          paragraphs: [
            "Our website does not use cookies or tracking technologies.",
          ],
        },
        {
          title: "6. Data Retention",
          paragraphs: [
            "Personal data is kept for as long as necessary to process your request or according to legal requirements.",
          ],
        },
        {
          title: "7. Data Sharing",
          paragraphs: [
            "Katrori ITS does not share your personal data with third parties, unless required by law.",
          ],
        },
        {
          title: "8. Your Rights",
          paragraphs: ["In accordance with the law, you have the right to:"],
          list: [
            "Request access to your data",
            "Correct inaccurate data",
            "Request deletion of data",
            "Request limitation of processing",
            "Withdraw your consent at any time",
          ],
          additional: [
            <>
              To exercise these rights, contact us at:{" "}
              <a href="mailto:info@katrori-its.com">info@katrori-its.com</a>
            </>,
          ],
        },
        {
          title: "9. Data Security",
          paragraphs: [
            "We take technical and organizational measures to protect your data from unauthorized access or misuse.",
          ],
        },
        {
          title: "10. Policy Changes",
          paragraphs: [
            "We may update this policy from time to time. Any changes will be published on this page.",
          ],
        },
        {
          title: "11. Contact",
          paragraphs: [
            "For any request related to personal data, you can contact us at:",
          ],
          list: [
            <>
              Email:{" "}
              <a href="mailto:info@katrori-its.com">info@katrori-its.com</a>
            </>,
            "Address: Lipjan, Kosovo",
            <>
              Tel: <a href="tel:+38338606806">+383 (0) 38 606 806</a>
            </>,
          ],
        },
      ],
    },
  };

  const currentContent = content[language];

  return (
    <div className="privacy-policy-page">
      <div className="privacy-policy-container">
        <div className="privacy-policy-header">
          <button
            className="language-toggle-btn"
            onClick={toggleLanguage}
            aria-label="Toggle Language"
          >
            <Languages className="language-icon" />
            <span>{language === "sq" ? "EN" : "SQ"}</span>
          </button>
          <h1>{currentContent.title}</h1>
          <div className="privacy-policy-company-info">
            <p className="company-name">Katrori ITS</p>
            <p>{currentContent.location}</p>
            <p>Email: info@katrori-its.com</p>
          </div>
        </div>

        <div className="privacy-policy-content">
          {currentContent.sections.map((section, index) => (
            <section key={index} className="privacy-section">
              <h2>{section.title}</h2>
              {section.paragraphs.map((para, pIndex) => (
                <p key={pIndex}>{para}</p>
              ))}
              {section.list && (
                <ul>
                  {section.list.map((item, lIndex) => (
                    <li key={lIndex}>{item}</li>
                  ))}
                </ul>
              )}
              {section.additional &&
                section.additional.map((para, aIndex) => (
                  <p key={`add-${aIndex}`}>{para}</p>
                ))}
            </section>
          ))}

          <div className="privacy-policy-footer">
            <p className="effective-date">{currentContent.effectiveDate}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
